import React, { useState, useEffect, useRef } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import Header from "../Header";
import Button from "../Button";
import styles from "./ReviewW9Info.module.css";
import useW9 from "./useW9";
import { useAccount } from "utils/AccountInfoContext";
import { useTranslation } from "react-i18next";

export function ReviewW9Info() {
  const {t} = useTranslation();
  const history = useHistory();
  const { id } = useParams();
  const { subEntityData, cachedIBOInfo } = useAccount();
  const selectedItem = subEntityData.find(
    (item) => item.entityId === parseInt(id)
  );
  const { useConfirmW9, useUploadW9 } = useW9(id);
  const confirmW9 = useConfirmW9.mutate;
  const uploadW9 = useUploadW9.mutate;

  const fileInputRef = useRef(null);
  // const [showW9Upload, setShowW9Upload] = useState(false);
  const [showW9Update, setShowW9Update] = useState(false);

  const [social, setSocial] = useState("");
  const [w9Status, setW9Status] = useState(null);

  useEffect(() => {
    if (selectedItem) {
      const ssn = selectedItem.ssn;
      const w9StatusFromApi = selectedItem.w9Status;

      setW9Status(w9StatusFromApi);

      if (
        w9StatusFromApi === 1 ||
        w9StatusFromApi === 2 ||
        w9StatusFromApi === 6 ||
        w9StatusFromApi === 7
      ) {
        setShowW9Update(true);
      }

      if (ssn) {
        setSocial(ssn);
      }
    }
  }, [selectedItem]);

  const handleSkipClick = () => {
    history.push("/all-set");
  };

  const handleHeaderClick = () => {
    history.push("/platinum-settings");
  };

  //Handles when W9 info is correct button clicked
  const handleW9ConfirmClick = () => {
    const entityId = selectedItem.entityId;
    confirmW9(
      { entityId },
      {
        onSuccess: () => {
          history.push("/all-set");
        },
        onError: (error) => {
          console.error(t("Error confirming W-9"), error);
        },
      }
    );
  };

  const handleUpdateW9Click = () => {
    setShowW9Update(false);
  };

  const handleCancel = () => {
    setShowW9Update(true);
  };

  const handleInstructionClick = async () => {
    try {
      const url =
        "https://wwguscms.blob.core.windows.net/files/cms/650/aeba5e30-6464-11ed-ac49-cb3ab1be5391/w9-instructions-20221114.pdf";
      window.open(url, "_blank");
    } catch (error) {
      console.error(t("Error opening the file"), error);
    }
  };

  const handleOpenW9Click = async () => {
    try {
      const url = "https://www.irs.gov/pub/irs-pdf/fw9.pdf";
      window.open(url, "_blank");
    } catch (error) {
      console.error(t("Error opening the file"), error);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  //Handles uploading of W9 or file
  const handleUploadFile = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const base64Image = await convertToBase64(file);

      const entityId = selectedItem.entityId;

      uploadW9(
        { entityId, fileData: base64Image },
        {
          onSuccess: () => {
            // setShowW9Upload(false);
            setShowW9Update(true);
            setW9Status(5); // Update status to 5 after upload
          },
          onError: (error) => {
            console.error(t("Error uploading W-9 document"), error);
          },
        }
      );
    } catch (error) {
      console.error(t("Error converting file to base64"), error);
    }
  };

  const convertToBase64 = async (file) => {
    return new Promise((resolve, reject) => {
      let fileReader = new FileReader();
      fileReader.onload = function () {
        return resolve(fileReader.result);
      };
      fileReader.readAsDataURL(file);
    });
  };

  const accountName = selectedItem?.name || "";

  const maskedSocial = social
    .split("")
    .map((char, index) =>
      index < social.length - 4 && /\d/.test(char) ? "*" : char
    )
    .join("");

  if (w9Status === 5) {
    return (
      <div className={styles.container}>
        <Header header="Platinum Settings" onClick={handleHeaderClick} />
        <div className={styles.flexContainer}>
          <span className={styles.header}>{t("Review W-9 Information")}</span>
        </div>
        <p className={styles.name}>{accountName}</p>
        <p className={styles.inReview}>
          {t("Your account submission is in review")}{" "}
          <a href="mailto:amm@wwdb.com">amm@wwdb.com</a>.
        </p>
        <div className={styles.lastNext}>
          <Button onClick={() => history.push("/all-set")} buttonText={t("Next")} />
        </div>
      </div>
    );
  }

  const pinLevelMapping = {
    2: "Platinum Settings",
    3: "Emerald Settings",
    4: "Diamond Settings",
  };

  const pinLevel = cachedIBOInfo?.PinLevel || 0;
  const headerText = pinLevelMapping[pinLevel];
  return (
    <div className={styles.container}>
      <Header header={t(headerText)} onClick={handleHeaderClick} />
      <div className={styles.flexContainer}>
        <span className={styles.header}>{t("Review W-9 Information")}</span>
        <Button
          onClick={handleSkipClick}
          buttonText={t("Skip")}
          className={styles.skipButton}
        />
      </div>

      {showW9Update ? (
        <div>
          <div className={styles.w9InfoContainer}>
            <span className={styles.accountName}>{accountName}</span>
            <div className={styles.w9Details}>
              <span className={styles.detailText}>
                {t("Tax Identification")}
                <br />
                {t("or SSN")}
              </span>
              <span className={styles.social}>{maskedSocial}</span>
            </div>
          </div>

          <div className={styles.buttonsContainer}>
            <Button
              onClick={handleW9ConfirmClick}
              buttonText={t("W-9 Information is Correct")}
              className={styles.ctrInfoButton}
            />
            <Button
              onClick={handleUpdateW9Click}
              buttonText={t("Update W-9")}
              className={styles.updateButton}
            />
          </div>
        </div>
      ) : (
        <>
          <div className={styles.w9UploadContainer}>
            <span className={styles.accountName}>{accountName}</span>
            <span className={styles.w9Header}>
              {t("Submit W-9 Information")}
            </span>
            <span className={styles.w9UpdateDetails}>
              <span className={styles.linkW9} onClick={handleInstructionClick}>
                {t("Instructions")}{" "}
              </span>
              {t("for adding a W-9")}
            </span>
            <span className={styles.w9UpdateDetails}>
              <span className={styles.linkW9} onClick={handleOpenW9Click}>
                {t("Open W-9")}{" "}
              </span>
              {t("in a separate window")}
            </span>
            <div className={styles.buttonsContainer}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleUploadFile}
              />
              <Button
                buttonText={t("Upload IRS Form")}
                onClick={handleUploadClick}
              />
              <Button onClick={handleCancel} buttonText={t("Cancel")} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default ReviewW9Info;
